// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import CreateProjectDialogContent from './settings/CreateProjectDialogContent';
import { toggleCreateDialog } from 'store/reducers/projectSettingsSlice';
import { useTranslation } from 'react-i18next';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import { UseCase } from '@tls/sw91-communication/types/com.base';
import useAppPermissions from 'hooks/useAppPermissions';
import NoLicenseDialogContent from './settings/NoLicenseDialogContent';
import { Dialog, SubMenuHorizontal } from '@tls/treact-ui';
import { FlagError } from '@tls/treact-ui/flags';
import styled from 'styled-components';

const ErrorFlag = FlagError;

enum Page {
  componentDetection,
  qualityControl,
}

export default function CreateProjectDialog() {
  const { id, show, duplicateSourceId } = useAppSelector(s => s.projectSettings);
  const { selectedProject } = useProjectsMetadata(id ?? duplicateSourceId);
  const [page, setPage] = useState(Page.componentDetection);
  const initializedDialogKeyRef = useRef<string | undefined>(undefined);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { hasCreateCDProjectPermission, hasCreateQCProjectPermission } = useAppPermissions();

  useEffect(() => {
    if (!show) {
      initializedDialogKeyRef.current = undefined;
      return;
    }

    const sourceId = id ?? duplicateSourceId;
    let dialogKey = `create:${String(hasCreateCDProjectPermission)}:${String(hasCreateQCProjectPermission)}`;
    if (sourceId) {
      dialogKey = `${id ? 'edit' : 'duplicate'}:${sourceId}`;
    }
    if (initializedDialogKeyRef.current === dialogKey) return;

    if (sourceId) {
      if (selectedProject?.projectId !== sourceId) return;
      const newPage =
        selectedProject.projectUseCase?.useCase === UseCase.COMPONENT_DETECTION
          ? Page.componentDetection
          : Page.qualityControl;
      setPage(newPage);
    } else {
      setPage(hasCreateCDProjectPermission ? Page.componentDetection : Page.qualityControl);
    }
    initializedDialogKeyRef.current = dialogKey;
  }, [selectedProject, id, duplicateSourceId, show, hasCreateCDProjectPermission, hasCreateQCProjectPermission]);

  const onTabClick = useCallback((newPage: Page) => () => setPage(newPage), []);

  const onClose = useCallback(() => {
    dispatch(toggleCreateDialog());
  }, [dispatch]);

  const getHeaderLabel = useCallback(
    (baseLabel: string) => {
      if (id) return 'project.settings.edit.title';
      if (duplicateSourceId) return 'project.settings.duplicate.title';
      return baseLabel;
    },
    [id, duplicateSourceId]
  );

  const header = useMemo(() => {
    const subMenus: ReactElement<React.ComponentProps<typeof SubMenuHorizontal.SubMenuTab>>[] = [];
    if (hasCreateCDProjectPermission) {
      subMenus.push(
        <SubMenuHorizontal.SubMenuTab
          label={t('project.settings.dialog.componentDetection.title')}
          selected={page === Page.componentDetection}
          onClick={onTabClick(Page.componentDetection)}
          key='CD'
          data-testid='project-settings-dialog-component-detection-tab'
        />
      );
    }
    if (hasCreateQCProjectPermission) {
      subMenus.push(
        <SubMenuHorizontal.SubMenuTab
          label={t('project.settings.dialog.qualityControl.title')}
          selected={page === Page.qualityControl}
          onClick={onTabClick(Page.qualityControl)}
          key='QC'
          icon={hasCreateQCProjectPermission ? null : <ErrorFlag width={'1.75rem'} />}
          data-testid='project-settings-dialog-quality-control-tab'
        />
      );
    }
    if (subMenus.length > 1) {
      return <SubMenuHorizontal>{subMenus}</SubMenuHorizontal>;
    }
    return null;
  }, [page, onTabClick, t, hasCreateCDProjectPermission, hasCreateQCProjectPermission]);

  const body = useMemo(() => {
    const sourceUseCase = selectedProject?.projectUseCase?.useCase;
    const sourceStrategy = selectedProject?.strategyType;
    const sourceForegroundLabelCount = selectedProject?.projectUseCase?.modelClasses.filter(
      modelClass => modelClass.classIndex !== 0
    ).length;

    if ((hasCreateCDProjectPermission && hasCreateQCProjectPermission) || id || duplicateSourceId) {
      return (
        <CreateProjectDialogContent
          page={page}
          sourceUseCase={sourceUseCase}
          sourceStrategy={sourceStrategy}
          sourceForegroundLabelCount={sourceForegroundLabelCount}
          saveOnEnter={show}
        />
      );
    } else if (hasCreateCDProjectPermission && !hasCreateQCProjectPermission) {
      return <CreateProjectDialogContent page={Page.componentDetection} saveOnEnter={show} />;
    } else if (!hasCreateCDProjectPermission && hasCreateQCProjectPermission) {
      return <CreateProjectDialogContent page={Page.qualityControl} saveOnEnter={show} />;
    } else {
      return <NoLicenseDialogContent />;
    }
  }, [
    hasCreateCDProjectPermission,
    hasCreateQCProjectPermission,
    show,
    page,
    id,
    duplicateSourceId,
    selectedProject?.projectUseCase?.useCase,
    selectedProject?.projectUseCase?.modelClasses,
    selectedProject?.strategyType,
  ]);

  return (
    <ProjectDialog enableClosing open={show} size='auto' onClose={onClose} data-testid='project-settings-dialog'>
      {!duplicateSourceId && header}
      {(id || duplicateSourceId) && (
        <h2 style={{ padding: '0.8rem', marginLeft: '0.5rem' }}>{t(getHeaderLabel(''))}</h2>
      )}
      {show && body}
    </ProjectDialog>
  );
}

const ProjectDialog = styled(Dialog)`
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  overflow: hidden;
`;
